import { useMemo, useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { ActionNotice, errorMessageOf } from '../../components/ui';
import { formatInteger } from '../system-admin/data';
import { customerLabel, filterCustomers } from './logic';
import type { PubCustomer } from './types';

const MAX_VISIBLE_CUSTOMERS = 50;

/** 닉네임·로그인 ID로 고객을 찾아 하나 고르는 목록. 서버 검색이 없어 목록을 불러와 클라이언트에서 거른다. */
export function CustomerPicker({
  customers,
  disabled,
  error,
  isLoading,
  onRetry,
  onSelect,
  selectedId,
}: {
  customers: readonly PubCustomer[];
  disabled?: boolean;
  error: unknown;
  isLoading: boolean;
  onRetry: () => void;
  onSelect: (customer: PubCustomer) => void;
  selectedId: string;
}) {
  const [keyword, setKeyword] = useState('');
  const filtered = useMemo(() => filterCustomers(customers, keyword), [customers, keyword]);
  const visible = filtered.slice(0, MAX_VISIBLE_CUSTOMERS);

  return (
    <div className="form-field">
      <span>고객</span>
      <input
        type="search"
        value={keyword}
        placeholder="닉네임 또는 로그인 ID로 검색"
        onChange={(event) => setKeyword(event.target.value)}
        disabled={disabled}
        aria-label="고객 검색"
      />
      {error ? (
        <ActionNotice tone="danger">
          {errorMessageOf(error, '고객 목록을 불러오지 못했습니다.')}{' '}
          <button className="secondary-button compact-button" type="button" onClick={onRetry}>
            <RefreshCcw size={14} />
            다시 시도
          </button>
        </ActionNotice>
      ) : isLoading ? (
        <div className="picker-empty">고객 목록을 불러오는 중입니다.</div>
      ) : visible.length === 0 ? (
        <div className="picker-empty">
          {customers.length === 0 ? '이 매장에 등록된 고객이 없습니다.' : '검색 결과가 없습니다.'}
        </div>
      ) : (
        <>
          <ul className="picker-list">
            {visible.map((customer) => (
              <li key={customer.id}>
                <button
                  type="button"
                  aria-pressed={customer.id === selectedId}
                  disabled={disabled}
                  onClick={() => onSelect(customer)}
                >
                  <span>
                    <strong>{customerLabel(customer)}</strong>
                    {customer.nickname && customer.internal_login_id ? (
                      <small> {customer.internal_login_id}</small>
                    ) : null}
                  </span>
                  <small>{formatInteger(customer.buy_in_points)} P</small>
                </button>
              </li>
            ))}
          </ul>
          {filtered.length > visible.length ? (
            <small className="muted-text">
              {filtered.length}명 중 {visible.length}명만 표시합니다. 검색어를 더 입력하세요.
            </small>
          ) : null}
        </>
      )}
    </div>
  );
}
